import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation, Trans } from 'react-i18next'

import { ReactComponent as StripeIcon } from '../../../assets/images/common/icons/stripe-icon.svg'
import { ReactComponent as StripePendingIcon } from '../../../assets/images/common/icons/check-circle-white.svg'
import { ReactComponent as StripeCompletedIcon } from '../../../assets/images/common/icons/check-circle-white.svg'
import { ReactComponent as LatoQuestionIcon } from '../../../assets/images/common/icons/lato-question-icon.svg'
import { checkLicensePermission } from '../../../utility/communityLicense'
import * as stripeConnectActions from '../../../actions/stripeConnectActions'

import './StripeConnection.scss'
import ConfirmationModal2 from '../../common/ConfirmationModal2'

const StripeConnection = () => {
  const { t } = useTranslation()

  const user = useSelector(state => state.user.current_user)
  const community = user.communities[0]

  const [loadingConnect, setLoadingConnect] = useState(false)
  const [loadingDisconnect, setLoadingDisconnect] = useState(false)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)

  const connectStripe = () => {
    setLoadingConnect(true)
    stripeConnectActions.connectToStripe()
      .then((res) => window.location.replace(res.data.account_link_url))
      .catch((_err) => {
        setLoadingConnect(false)
      })
  }

  const disconnectStripeAccount = () => {
    setLoadingDisconnect(true)
    stripeConnectActions.removeStripeConnectedAccount(community.stripe_connected_accounts[0]?.id)
      .finally(() => { setShowConfirmationModal(false); setLoadingDisconnect(false) })
  }

  return (
    <div id='StripeConnection' className='d-flex align-items-center'>
      {(community.selected_stripe_connected_account && (
        <>
          <div className='d-flex align-items-center'>
            <StripeCompletedIcon id='stripeCompletedIcon' className='stripe-icons' />
            <div className='d-flex flex-column justify-content-center'>
              <p className='primary-stripe-text'>
                {t('communitySettings.pricing.stripe.verifiedAndConnected')}
              </p>
              <p className='secondary-stripe-text'>
                <Trans
                  i18nKey='communitySettings.pricing.stripe.paymentsEnabledConnectedAs'
                  components={{ emailSpan: <span style={{ textDecoration: 'underline' }} /> }}
                  values={{ email: community.selected_stripe_connected_account.email }}
                />
              </p>
            </div>
          </div>
          {community.selected_stripe_connected_account.can_be_deleted &&
            <button
              onClick={() => setShowConfirmationModal(true)}
              className='ml-auto stripe-disconnect-button'
              disabled={loadingConnect || loadingDisconnect}
            >
              {loadingDisconnect
                ? <span> <i className='fa fa-spinner fa-spin' style={{ marginRight: '5px' }} aria-hidden='true' />
                  {t('communitySettings.pricing.stripe.disconnecting')}
                </span>
                : t('communitySettings.pricing.stripe.disconnect')
              }
            </button>
          }
          <ConfirmationModal2
            showModal={showConfirmationModal}
            closeModal={() => setShowConfirmationModal(false)}
            onDeny={() => setShowConfirmationModal(false)}
            denyMessage={t('common.cancel')}
            onProceed={disconnectStripeAccount}
            proceedMessage={t('communitySettings.pricing.stripe.disconnectShort')}
            icon={<i className='fa fa-question-circle' style={{ fontSize: '72px' }} />}
            title={t('communitySettings.pricing.stripe.disconnectModalTitle')}
            message={
              checkLicensePermission(community.license_type, ['standard', 'standard_legacy'])
                ? t('communitySettings.pricing.stripe.disconnectMessageStandard')
                : t('communitySettings.pricing.stripe.disconnectMessageOther')
            }
          />
        </>
      )) || (community.stripe_connected_accounts[0]?.status === 'attached' && (
        <>
          <div className='d-flex align-items-center'>
            <StripePendingIcon id='stripePendingIcon' className='stripe-icons' />
            <div className='d-flex flex-column justify-content-center'>
              <p className='primary-stripe-text'>{t('communitySettings.pricing.stripe.integrationNotReady')}</p>
              <p className='secondary-stripe-text'>
                <Trans
                  i18nKey='communitySettings.pricing.stripe.connectedAsCompleteOnboarding'
                  components={{
                    emailSpan: <span style={{ textDecoration: 'underline' }} />,
                    iconSpan: <span><LatoQuestionIcon className='lato-question-icon' /></span>,
                  }}
                  values={{ email: community.stripe_connected_accounts[0].email }}
                />
              </p>
            </div>
          </div>
          <div className='d-flex ml-auto stripe-buttons'>
            <button onClick={connectStripe} className='stripe-connect-button' disabled={loadingConnect || loadingDisconnect}>
              {loadingConnect
                ? <span>
                  <i className='fa fa-spinner fa-spin' style={{ marginRight: '5px' }} aria-hidden='true' />
                  {t('communitySettings.pricing.stripe.connecting')} </span>
                : t('communitySettings.pricing.stripe.checkApplication')
              }
            </button>
            {community.stripe_connected_accounts[0].can_be_deleted &&
              <button onClick={disconnectStripeAccount} className='stripe-disconnect-button' disabled={loadingConnect || loadingDisconnect}>
                {loadingDisconnect
                  ? <span> <i className='fa fa-spinner fa-spin' style={{ marginRight: '5px' }} aria-hidden='true' />
                    {t('communitySettings.pricing.stripe.disconnecting')} </span>
                  : t('communitySettings.pricing.stripe.disconnect')
                }
              </button>
            }
          </div>
        </>
      )) || (
        <>
          <div className='d-flex align-items-center'>
            <StripeIcon id='stripeIcon' className='stripe-icons' />
            <div className='d-flex flex-column justify-content-center'>
              <p className='primary-stripe-text'>
                <Trans
                  i18nKey='communitySettings.pricing.stripe.wantOnlinePayments'
                  components={{ iconSpan: <span><LatoQuestionIcon className='lato-question-icon' /></span> }}
                />
              </p>
              <p className='secondary-stripe-text'>{t('communitySettings.pricing.stripe.connectForCollection')}</p>
            </div>
          </div>
          <button onClick={connectStripe} className='ml-auto stripe-connect-button' disabled={loadingConnect || loadingDisconnect}>
            {loadingConnect
              ? <span>
                <i className='fa fa-spinner fa-spin' style={{ marginRight: '5px' }} aria-hidden='true' />
                {t('communitySettings.pricing.stripe.connecting')}
              </span>
              : t('communitySettings.pricing.stripe.connectToStripe')
            }
          </button>
        </>
      )}
    </div>
  )
}

export default StripeConnection
